import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { comics } from '@/mocks/comics';
import { useSeo } from '@/hooks/useSeo';

const tabs = ['日榜', '周榜', '月榜', '总榜'];

export default function Ranking() {
  useSeo({
    title: '动漫之家漫画网排行榜 - 热门推荐人气榜单',
    description:
      '查看动漫之家漫画网排行榜，实时热门推荐人气榜单，综合阅读与收藏热度排序，快速发现最值得追的热门漫画。',
    canonicalPath: '/ranking',
  });

  const [tab, setTab] = useState('日榜');

  const list = useMemo(() => {
    const sorted = [...comics].sort((a, b) => b.heat - a.heat);
    if (tab === '日榜') return sorted.slice(0, 10);
    if (tab === '周榜') return [...comics].sort((a, b) => b.rating - a.rating).slice(0, 12);
    if (tab === '月榜') return [...comics].sort((a, b) => b.chapters - a.chapters).slice(0, 14);
    return sorted;
  }, [tab]);

  return (
    <div>
      {/* Page head */}
      <section className="border-b border-background-200 bg-background-100">
        <div className="mx-auto max-w-7xl px-4 py-8 md:px-6 md:py-10">
          <nav className="flex items-center gap-1.5 text-sm text-foreground-500" aria-label="面包屑">
            <Link to="/" className="transition-colors hover:text-primary-600">首页</Link>
            <i className="ri-arrow-right-s-line"></i>
            <span className="text-foreground-700">动漫之家排行榜</span>
          </nav>
          <div className="mt-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <span className="inline-flex items-center gap-2 text-sm font-medium text-secondary-600">
                <i className="ri-trophy-line"></i> 人气榜单
              </span>
              <h1 className="mt-2 font-heading text-2xl text-foreground-950 md:text-3xl">动漫之家漫画网排行榜</h1>
              <p className="mt-2 text-sm text-foreground-500">
                综合阅读热度、收藏量与读者讨论度动态排序，帮你快速锁定人气热门推荐。
              </p>
            </div>
          </div>
          <div className="mt-6 inline-flex rounded-full border border-background-300 bg-background-50 p-1">
            {tabs.map((t) => (
              <button
                key={t}
                type="button"
                onClick={() => setTab(t)}
                className={`rounded-full px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                  tab === t ? 'bg-primary-500 text-background-50' : 'text-foreground-600 hover:bg-background-100'
                }`}
              >
                {t}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Ranking list */}
      <section className="mx-auto max-w-7xl px-4 py-10 md:px-6" aria-label="排行榜单">
        <div className="flex items-center gap-2 text-sm text-foreground-500">
          <i className="ri-fire-fill text-primary-500"></i> 当前展示 <span className="font-semibold text-foreground-900">{tab}</span>，共 {list.length} 部作品
        </div>
        <ol className="mt-6 space-y-3">
          {list.map((comic, idx) => (
            <li key={comic.id}>
              <Link
                to={`/comic/${comic.id}`}
                className="group flex items-center gap-4 rounded-lg border border-background-200 bg-background-100 p-3 transition-all hover:border-primary-300 hover:bg-background-50 md:gap-6 md:p-4"
              >
                <span
                  className={`font-heading text-2xl w-12 shrink-0 text-center md:text-3xl ${
                    idx === 0 ? 'text-primary-500' : idx < 3 ? 'text-accent-600' : 'text-background-400'
                  }`}
                >
                  {idx + 1}
                </span>
                <img src={comic.cover} alt={`${comic.title}排行榜封面`} className="h-20 w-14 shrink-0 rounded-md object-cover md:h-24 md:w-16" />
                <div className="min-w-0 flex-1">
                  <h2 className="truncate text-base font-bold text-foreground-950 transition-colors group-hover:text-primary-600">
                    {comic.title}
                  </h2>
                  <p className="mt-1 truncate text-sm text-foreground-500">{comic.description}</p>
                  <div className="mt-2 flex flex-wrap items-center gap-2">
                    <span className="rounded-full bg-secondary-100 px-2 py-0.5 text-xs text-secondary-700">{comic.genre}</span>
                    {comic.tags.slice(0, 2).map((t) => (
                      <span key={t} className="rounded-full bg-background-200 px-2 py-0.5 text-xs text-foreground-600">{t}</span>
                    ))}
                    <span className={`ml-auto flex items-center gap-1 text-xs font-medium ${comic.status === '已完结' ? 'text-accent-600' : 'text-primary-600'}`}>
                      <i className="ri-flag-line"></i> {comic.status}
                    </span>
                  </div>
                </div>
                <div className="hidden w-24 shrink-0 flex-col items-end gap-1 sm:flex">
                  <span className="flex items-center gap-1 text-lg font-bold text-accent-600">
                    <i className="ri-star-fill text-sm text-accent-500"></i>
                    {comic.rating.toFixed(1)}
                  </span>
                  <span className="text-xs text-foreground-400">热度 {(comic.heat / 10000).toFixed(1)}万</span>
                </div>
              </Link>
            </li>
          ))}
        </ol>

        <div className="mt-10 rounded-lg border border-primary-200 bg-primary-50 p-6 text-center">
          <h3 className="font-heading text-lg text-primary-700">榜单还在持续更新中</h3>
          <p className="mx-auto mt-2 max-w-lg text-sm text-foreground-600">
            想按类型找更多作品？前往分类大全页，或直接搜索你感兴趣的关键词，发现更多好漫画。
          </p>
          <div className="mt-4 flex flex-wrap justify-center gap-3">
            <Link to="/genre" className="whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-50 hover:bg-primary-600">去分类浏览</Link>
            <Link to="/completed" className="whitespace-nowrap rounded-lg border border-primary-300 px-5 py-2.5 text-sm font-medium text-primary-700 hover:bg-primary-100">看完结漫画</Link>
          </div>
        </div>
      </section>

      {/* BreadcrumbList JSON-LD */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'BreadcrumbList',
            itemListElement: [
              { '@type': 'ListItem', position: 1, name: '首页', item: `${window.location.origin}${__BASE_PATH__}/` },
              { '@type': 'ListItem', position: 2, name: '动漫之家排行榜' },
            ],
          }),
        }}
      />
    </div>
  );
}